/**
 * Demo data inject/cleanup for the Operator Panel segment of the Welcome Tour.
 *
 * Injects:
 * - Demo machine into machines list + selectedItem
 * - Demo batch statistics into batch reducer
 * - Demo connector state (loaded: true, Machines worker Online)
 * - Registry entries for batch (intercepted by reducer guards)
 * - setTourActive(true) to block getMachines/updateMachineInfo during tour
 */

import { AppThunk } from '@store/types';
import { setBatch } from '@reducers/batch';
import { setSelectedMachine, injectDemoMachine, removeDemoMachine } from '@reducers/machines';
import { connectorInfoSuccess } from '@reducers/connector';
import {
  registerDemoBatch,
  clearDemoRegistry,
  setTourActive,
} from '../../../../helpers/demoDataRegistry';
import { DEMO_WELCOME_MACHINE_ID, DEMO_BATCH_ID, DEMO_OPERATION_ID } from '@components/tours/config/global/welcome/constants';
import {
  createDemoMachine,
  createDemoBatchStatistics,
  createDemoConnector,
} from './fixtures';

// Store originals for restoration
let originalBatch: any = null;
let originalSelectedMachine: any = null;
let originalConnector: any = null;

/**
 * Inject demo data for the Operator Panel segment.
 */
export const injectOperatorPanelDemo = (): AppThunk => (dispatch, getState) => {
  const state = getState();

  // Save originals for cleanup
  originalBatch = state.batch?.batch?.data;
  originalSelectedMachine = state.machines?.selectedItem;
  originalConnector = state.connector?.connectorDetails;

  // Block real API calls (getMachines, updateMachineInfo, etc.) during tour
  setTourActive(true);

  // Override for OP: use 'Linked' status so Counter renders instead of NCFileCentralPart,
  // and add links so piece counter shows target (4/25)
  const batchStats = {
    ...createDemoBatchStatistics(),
    ncFileStatus: 'Linked',
    linkStatus: 'Linked',
    links: [
      {
        productionOperationId: DEMO_OPERATION_ID,
        quantityTarget: 25,
        productionBatchId: DEMO_BATCH_ID,
      },
    ],
  };
  registerDemoBatch(DEMO_BATCH_ID, batchStats);

  // Inject demo machine with 'Linked' ncFileStatus for OP
  const baseMachine = createDemoMachine();
  const demoMachine = {
    ...baseMachine,
    machineStatus: {
      ...baseMachine.machineStatus,
      ncFileStatus: 'Linked',
    },
  };
  dispatch(injectDemoMachine(demoMachine));
  dispatch(setSelectedMachine(demoMachine));

  // Set batch data directly (for components that read from Redux without refetching)
  dispatch(setBatch(batchStats));

  // Inject connector state so OP doesn't show unavailable screen
  const connectorData = createDemoConnector();
  dispatch(connectorInfoSuccess({ ...connectorData, isPRODUCTIONExist: false }));
};

/**
 * Cleanup demo data after segment ends.
 */
export const cleanupOperatorPanelDemo = (): AppThunk => (dispatch) => {
  // Clear registry + tour active flag (stops intercepting API calls)
  clearDemoRegistry();
  setTourActive(false);

  // Remove demo machine
  dispatch(removeDemoMachine(DEMO_WELCOME_MACHINE_ID));

  // Restore original state
  if (originalSelectedMachine && originalSelectedMachine.machineId !== DEMO_WELCOME_MACHINE_ID) {
    dispatch(setSelectedMachine(originalSelectedMachine));
  } else {
    dispatch(setSelectedMachine({}));
  }

  if (originalBatch && originalBatch.productionBatchId !== DEMO_BATCH_ID) {
    dispatch(setBatch(originalBatch));
  } else {
    dispatch(setBatch(null));
  }

  // Restore connector state
  if (originalConnector) {
    dispatch(connectorInfoSuccess(originalConnector));
  }

  // Clear stored refs
  originalBatch = null;
  originalSelectedMachine = null;
  originalConnector = null;
};
